import { useEnvironment } from "@/queries/environment-queries";
import { useRef, useState, type FC } from "react";
import { Agents, useAgentExecutionsByEnvironment } from "@/queries/agent-queries";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { ErrorMessage } from "@/components/ErrorMessage";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { EnvironmentOnboardingStepId, useEnvironmentOnboardingSteps } from "@/hooks/use-environment-onboarding";
import { ConversationMessageCreateProject } from "@/components/environments/onboarding/ConversationMessageCreateProject";
import { ConversationMessageStartSetup } from "@/components/environments/onboarding/ConversationMessageStartSetup";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { ConversationMessageLoading } from "@/components/environments/onboarding/ConversationMessageLoading";
import { ConversationMessageStartEnvironment } from "@/components/environments/onboarding/ConversationMessageStartEnvironment";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { ConversationMessageCommitFiles } from "@/components/environments/onboarding/ConversationMessageCommitFiles";
import { AgentExecutionModal } from "@/components/ai/chat/AgentExecutionModal";
import { Text } from "@/components/podkit/typography/Text";
import { EnvironmentOnboardingDropdownMenu } from "@/components/environments/onboarding/EnvironmentOnboardingDropdownMenu";

export const EnvironmentOnboardingConversation: FC<{ environmentId: string }> = ({ environmentId }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [agentExecutionModalOpen, setAgentExecutionModalOpen] = useState(false);

    const { data: environment, isPending: isEnvironmentPending } = useEnvironment(environmentId);
    const { data: agentExecution, isPending: isAgentExecutionPending } = useAgentExecutionsByEnvironment({
        environmentId,
        agentId: Agents.DevelopmentEnvironmentSetup.id,
    });
    const { error, messages } = useStreamConversation(agentExecution?.id, agentExecution?.status?.conversationUrl);

    const { defaultActive, isLoading: isLoadingDefaultActive } = useEnvironmentOnboardingSteps(environment?.id);
    const isPending = isEnvironmentPending || isAgentExecutionPending;
    const hasExistingConfiguration = !isLoadingDefaultActive && defaultActive === EnvironmentOnboardingStepId.Share;
    const hasAgentExecution = !isAgentExecutionPending && agentExecution !== undefined;
    const createdFromProject = environment?.metadata?.projectId;

    // We only show the onboarding section for environments that are not associated with a project,
    // unless there's an agent execution - in that case we keep it visible to show the conversation
    // even after project creation.
    if (isPending || (createdFromProject && !hasAgentExecution)) {
        return null;
    }

    // TODO(NEXT-3751): We need a more reliable signal, e.g. have the agent send a __GITPOD_COMPLTED_DEV_SETUP_MARKER__ marker which we'd then have to
    // parse out of the agent stream.
    const isAgentExecutionDone =
        agentExecution?.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution?.status?.phase === AgentExecution_Phase.STOPPED;

    const isStarting =
        environment?.spec?.desiredPhase === EnvironmentPhase.RUNNING &&
        environment?.status?.phase !== EnvironmentPhase.STOPPED && // stopped/stopping for auto-stopped
        environment?.status?.phase !== EnvironmentPhase.STOPPING &&
        environment?.status?.phase !== EnvironmentPhase.RUNNING &&
        environment?.status?.phase !== EnvironmentPhase.UPDATING;

    const environmentIsRunningOrUpdating =
        environment?.spec?.desiredPhase === EnvironmentPhase.RUNNING &&
        (environment?.status?.phase === EnvironmentPhase.RUNNING ||
            environment?.status?.phase === EnvironmentPhase.UPDATING);

    const isStopped =
        environment?.spec?.desiredPhase !== EnvironmentPhase.RUNNING ||
        environment?.status?.phase === EnvironmentPhase.STOPPED ||
        environment?.status?.phase === EnvironmentPhase.STOPPING;

    const showStartSetupMessage =
        !isAgentExecutionPending && environmentIsRunningOrUpdating && (!hasExistingConfiguration || hasAgentExecution);

    const showCreateProjectMessage =
        environmentIsRunningOrUpdating &&
        hasExistingConfiguration &&
        (isAgentExecutionDone || !hasAgentExecution) &&
        !createdFromProject; // Don't show if project already exists

    const showCommitFilesMessage =
        environmentIsRunningOrUpdating &&
        hasAgentExecution &&
        (defaultActive === EnvironmentOnboardingStepId.CommitFiles ||
            defaultActive === EnvironmentOnboardingStepId.Share);

    return (
        <div
            data-testid="environment-onboarding-conversation"
            className="flex h-full min-w-[100px] flex-col rounded-lg border-0.5 border-border-base bg-surface-01 pb-1 shadow-content-surface dark:shadow-none"
        >
            <div className="border-b-0.5 border-border-base py-6">
                <div className="flex h-6 items-center justify-between px-8">
                    <Text className="text-base font-bold">Automated environment setup</Text>
                    {agentExecution && (
                        <EnvironmentOnboardingDropdownMenu
                            agentExecution={agentExecution}
                            onClickViewConversation={() => setAgentExecutionModalOpen(true)}
                        />
                    )}
                </div>
            </div>
            <ScrollArea
                orientation="vertical"
                autoScroll={true}
                autoScrollContentRef={contentRef}
                className="mx-[2px] my-[2px] h-full pl-[var(--scrollbar-width)]"
            >
                <div ref={contentRef} className="flex min-h-full flex-col gap-6 px-6 py-5">
                    <ErrorMessage error={error} />
                    {isStarting && <ConversationMessageLoading />}
                    {isStopped && <ConversationMessageStartEnvironment />}
                    {showStartSetupMessage && (
                        <ConversationMessageStartSetup
                            agentExecution={agentExecution}
                            isAgentExecutionDone={isAgentExecutionDone}
                            environmentId={environmentId}
                            messages={messages}
                        />
                    )}
                    {showCommitFilesMessage && (
                        <ConversationMessageCommitFiles
                            agentExecution={agentExecution}
                            isAgentExecutionDone={isAgentExecutionDone}
                            environment={environment}
                            messages={messages}
                        />
                    )}
                    {showCreateProjectMessage && (
                        <ConversationMessageCreateProject
                            environment={environment}
                            hasAgentExecution={hasAgentExecution}
                        />
                    )}
                </div>
            </ScrollArea>
            {agentExecution && (
                <AgentExecutionModal
                    agentExecution={agentExecution}
                    open={agentExecutionModalOpen}
                    onClose={() => setAgentExecutionModalOpen(false)}
                />
            )}
        </div>
    );
};
